import React, { useEffect, useState } from 'react';

import { Tabs } from '@mantine/core';
import { IconCreditCard, IconRoute, IconSettings, IconShoppingCart, IconTag, IconUserCog } from '@tabler/icons-react';
import { DashboardPageWrapper } from 'components/DashboardPageWrapper';
import { TabPanelWrapper } from 'components/TabPanelWrapper';
import { useUserContext } from 'contexts/UserContext';
import { ChartAverageIcon, Key02Icon, PuzzleIcon } from 'hugeicons-react';
import { ApiSettingsTab } from 'pages/Settings/SettingTabs/ApiSettingsTab';
import { BillingSettingsTab } from 'pages/Settings/SettingTabs/BillingSettingsTab/BillingSettingsTab';
import { ByokTab } from 'pages/Settings/SettingTabs/ByokTab';
import { GeneralSettingsTab } from 'pages/Settings/SettingTabs/GeneralSettingsTab/GeneralSettingsTab';
import { IntegrationsSettingsTab } from 'pages/Settings/SettingTabs/IntegrationsSettingsTab/IntegrationsSettingsTab';
import { StoresSettingsTab } from 'pages/Settings/SettingTabs/StoresSettingsTab/StoresSettingsTab';
import { UsageSettingsTab } from 'pages/Settings/SettingTabs/UsageSettingsTab/UsageSettingsTab';
import { WhiteLabelSettingsTab } from 'pages/Settings/SettingTabs/WhiteLabelSettingsTab/WhiteLabelSettingsTab';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { UserService } from 'services/user-service';
import { SELF_MANAGING_CUSTOM_ORGANISATIONS } from 'shared/constants/organisation';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { UserAccess } from 'shared/interfaces/User';

const TAB_PATH = {
  GENERAL: 'general',
  PASSWORD: 'password',
  USAGE: 'usage',
  API: 'api',
  WHITE_LABEL: 'white-label',
  STORES: 'stores',
  BYOK: 'byok',
  // ORGANISATION: 'organisation',
  BILLING: 'billing',
  INTEGRATIONS: 'integrations'
};

type SettingsTab = {
  value: string;
  component: React.ReactNode;
  icon: React.ReactNode;
  label: string;
  disableForCustomOrganisation: boolean;
  visible: boolean;
  minAccess: UserAccess;
};

export function SettingsPage() {
  const { t } = useTranslation();
  const { tabName } = useParams();
  const navigate = useNavigate();
  const [user] = useUserContext();

  const getActiveTabFromPath = (tab?: string) => {
    // Verify that the tabName is valid
    if (!tab) return TAB_PATH.GENERAL;
    if (!Object.values(TAB_PATH).includes(tab)) return TAB_PATH.GENERAL;

    // Verify if user has access to api tab
    if (tab === TAB_PATH.API && UserService.hasUserAccessWithMinRole(UserAccess.ADMIN)) return TAB_PATH.API;

    // Verify if user has access to white label tab
    if (tab === TAB_PATH.WHITE_LABEL && UserService.hasUserAccessWithMinRole(UserAccess.SUPER_ADMIN))
      return TAB_PATH.WHITE_LABEL;

    // Verify if user has access to billing tab
    if (tab === TAB_PATH.BILLING && UserService.hasUserAccessWithMinRole(UserAccess.SUPER_ADMIN))
      return TAB_PATH.BILLING;

    // if (tab === TAB_PATH.ORGANISATION && UserService.hasUserAccessWithMinRole(UserAccess.SUPER_ADMIN))
    //   return TAB_PATH.ORGANISATION;
    // if (tab === TAB_PATH.BILLING || tab === TAB_PATH.ORGANISATION) {
    //   return TAB_PATH.PROFILE; // }

    // Redirect restricted tabs to profile
    if (tab === TAB_PATH.BILLING) {
      return TAB_PATH.GENERAL;
    }

    return tab;
  };

  const [activeTab, setActiveTab] = useState<string>(getActiveTabFromPath(tabName));

  useEffect(() => {
    const activeTabFromPath = getActiveTabFromPath(tabName);
    setActiveTab(activeTabFromPath);
  }, [tabName]);

  const TABS: SettingsTab[] = [
    {
      value: TAB_PATH.GENERAL,
      component: <GeneralSettingsTab />,
      icon: <IconUserCog />,
      label: t('SETTINGS.GENERAL.TITLE'),
      disableForCustomOrganisation: false,
      visible: true,
      minAccess: UserAccess.MEMBER
    },
    // {
    //   value: TAB_PATH.ORGANISATION,
    //   component: <OrganisationSettingsTab />,
    //   icon: <IconBuilding size={30} />,
    //   label: t('SETTINGS.ORGANISATION.TITLE'),
    //   disableForCustomOrganisation: false,
    //   visible: true,
    //   minAccess: UserAccess.SUPER_ADMIN
    // },
    {
      value: TAB_PATH.INTEGRATIONS,
      component: <IntegrationsSettingsTab />,
      icon: <PuzzleIcon size={20} />,
      label: t('INTEGRATIONS.TITLE'),
      disableForCustomOrganisation: false,
      visible: true,
      minAccess: UserAccess.ADMIN
    },
    {
      value: TAB_PATH.API,
      component: <ApiSettingsTab />,
      icon: <Key02Icon size={20} />,
      label: t('API.TITLE'),
      disableForCustomOrganisation: false,
      visible: true,
      minAccess: UserAccess.ADMIN
    },
    {
      value: TAB_PATH.STORES,
      component: <StoresSettingsTab />,
      icon: <IconShoppingCart />,
      label: t('STORES.TITLE'),
      disableForCustomOrganisation: false,
      visible: true,
      minAccess: UserAccess.ADMIN
    },
    {
      value: TAB_PATH.WHITE_LABEL,
      component: <WhiteLabelSettingsTab />,
      icon: <IconTag />,
      label: t('WHITE_LABEL.TITLE'),
      disableForCustomOrganisation: false,
      visible: true,
      minAccess: UserAccess.SUPER_ADMIN
    },
    {
      value: TAB_PATH.USAGE,
      component: <UsageSettingsTab />,
      icon: <ChartAverageIcon size={20} />,
      label: t('USAGE.TITLE'),
      disableForCustomOrganisation: false,
      visible: true,
      minAccess: UserAccess.MEMBER
    },
    {
      value: TAB_PATH.BYOK,
      component: <ByokTab />,
      icon: <IconRoute />,
      label: t('SETTINGS.BYOK.TITLE'),
      disableForCustomOrganisation: false,
      visible: true,
      minAccess: UserAccess.SUPER_ADMIN
    },
    {
      value: TAB_PATH.BILLING,
      component: <BillingSettingsTab />,
      icon: <IconCreditCard />,
      label: t('SETTINGS.BILLING.TITLE'),
      disableForCustomOrganisation: !SELF_MANAGING_CUSTOM_ORGANISATIONS.includes(user.organisation),
      visible: true,
      minAccess: UserAccess.SUPER_ADMIN
    }
  ];

  const filteredTabs = TABS.filter((tab) => tab.visible && UserService.hasUserAccessWithMinRole(tab.minAccess));

  return (
    <DashboardPageWrapper icon={<IconSettings style={{ width: 25, height: 25 }} />} title={t('SETTINGS.TITLE')}>
      <Tabs value={activeTab} inverted onTabChange={(tabPath) => navigate(`${ROUTE_LINKS.settings}/${tabPath}`)}>
        <Tabs.List>
          {filteredTabs.map((tab, key: number) => {
            if (tab.disableForCustomOrganisation && UserService.isCustomOrganisationMember()) return null;
            if (UserService.hasUserAccessWithMinRole(tab.minAccess)) {
              return (
                <Tabs.Tab value={tab.value} icon={tab.icon} key={key}>
                  {tab.label}
                </Tabs.Tab>
              );
            }
          })}
        </Tabs.List>
        {filteredTabs.map((tab, key: number) => {
          if (tab.disableForCustomOrganisation && UserService.isCustomOrganisationMember()) return null;
          if (UserService.hasUserAccessWithMinRole(tab.minAccess)) {
            return (
              <Tabs.Panel key={key} value={tab.value}>
                <TabPanelWrapper>{tab.component}</TabPanelWrapper>
              </Tabs.Panel>
            );
          }
        })}
      </Tabs>
    </DashboardPageWrapper>
  );
}
